<template>
  <v-app v-if="isSpecialRoute()" id="app">
    <router-view />
  </v-app>
  <v-app v-else id="app" style="overflow-x: hidden">
    <NavigationDrawer
      :drawer.sync="drawer"
      :minimizedDrawer.sync="minimizedDrawer"
      :permissionsLoaded="permissionsLoaded"
      :darkTheme="darkTheme"
      @navigate="navigateTo"
      @close-nav="closeNav"
      @reload-and-refresh="reloadAndRefresh"
      @back-to-bjjlink="backToBjjlink"
      @to-tablet="toTablet"
      @go-to-releases="goToReleases"
      @show-welcome-dialog="setShowWelcomeDialog()"
      @show-migration-dialog="setShowMigrationDialog()"
      @start-onboarding="startOnboarding()"
    />

    <NavigationNavbar
      :minimizedDrawer="minimizedDrawer"
      :quickSearchQuery="quickSearchQuery"
      :loadingQuickSearch="loadingQuickSearch"
      :filteredQuickSearchItems="filteredQuickSearchItems"
      :paymentsReady="paymentsReady"
      :darkTheme="darkTheme"
      :appVersion="appVersion"
      :stripeAccountId="stripeAccountId"
      :crumbs="crumbs"
      @update:quickSearchQuery="quickSearchQuery = $event"
      @go-to-quick-search-item="goToQuickSearchItem"
      @navigate="navigateTo"
      @open-drawer="openDrawerMobile"
      @toggle-theme="toggleThemeColor"
      @toggle-admin-plus="toggleAdminPlusVersion"
      @open-feedback="sendFeedbackDialog = true"
      @open-tech-support="openTechSupport"
      @logout="logout"
      @reload-refresh="reloadAndRefresh"
      @copy="copyToClipboard"
    />
    <v-main>
      <v-btn
        class="app-floating-back-btn"
        :class="{
          'd-none': !$store.state.adminPlusVersion || isSmallerDisplay(),
        }"
        icon
        @click="minimizedDrawer = !minimizedDrawer"
      >
        <v-icon size="24" :color="darkTheme ? '#BDBDBD' : '#212121'">{{
          !minimizedDrawer ? 'mdi-chevron-left' : 'mdi-chevron-right'
        }}</v-icon>
      </v-btn>

      <v-breadcrumbs
        v-if="crumbs && crumbs.length && !$store.state.adminPlusVersion"
        :items="crumbs"
      >
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            class="clickable"
            @click="breadcrumbRoute(item)"
            :disabled="item.disabled"
          >
            {{ item.crumbName }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
      <router-view
        @refresh-search="refreshQuickSearch"
        @start-onboarding="startOnboarding"
        @expand-nav-for-tour="expandNavForTour"
        class="pa-0 ma-0"
        v-show="!isSmallerDisplay() || minimizedDrawer"
        v-if="permissionsLoaded"
      />

      <div v-if="isSmallerDisplay() && !minimizedDrawer">
        <img
          class="rotated"
          style="
            width: 335px;
            left: -105px;
            top: 30%;
            opacity: 0.25;
            position: absolute;
          "
          :src="
            isDarkMode()
              ? assets('logos/bjjlink-logo-horizontal-white.png')
              : assets('logos/bjjlink-logo-horizontal-black.png')
          "
        />
      </div>
    </v-main>
    <v-dialog v-model="sendFeedbackDialog" width="80%">
      <v-card outlined color="plusSurface">
        <v-card-title class="plusTextPrimary--text">
          {{ $t('message.feedback_questions') }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            outlined
            width="100%"
            v-model="feedbackContents"
            :label="$t('message.contents')"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-btn
            elevation="0"
            small
            @click="
              sendFeedbackDialog = false;
              feedbackContents = null;
            "
            >{{ $t('message.cancel') }}</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            color="plusActionButton"
            dark
            small
            @click="sendFeedback()"
            >{{ $t('message.send') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="techSupportDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <div class="pa-0">
        <v-toolbar dense dark color="secondary" class="pa-0">
          <v-spacer></v-spacer>
          <v-btn icon small @click="techSupportDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <div>
          <iframe
            class="support-iframe"
            src="https://bjjlink.tawk.help/"
          ></iframe>
        </div>
      </div>
    </v-dialog>
    <v-dialog v-model="newAppAlertDialog" max-width="300">
      <v-card outlined>
        <v-card-title
          ><v-icon class="mr-1" color="error">mdi-alert-outline</v-icon>
          {{ $t('message.update_your_app_title') }}
        </v-card-title>
        <div
          v-if="
            isSmallerDisplay() && (getOS() == 'Android' || getOS() == 'iOS')
          "
        >
          <v-card-text class="mt-3">
            {{ $t('message.update_your_app_mobile') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              elevation="0"
              color="primary"
              outlined
              small
              @click="downloadNewApp()"
            >
              <v-icon class="mr-1">mdi-download-circle-outline</v-icon>
              {{ $t('message.copy_link_for_new_app') }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
        <div v-else>
          <v-card-text class="mt-3">
            {{ $t('message.update_your_app_url') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              elevation="0"
              color="primary"
              outlined
              small
              @click="reloadNewApp()"
            >
              <v-icon class="mr-1">mdi-sync</v-icon>
              {{ $t('message.reload') }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small @click="newAppAlertDialog = false">
            {{ $t('message.later') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <WelcomeDialog :show="showWelcomeDialog" @close="dismissWelcome" />
    <MigrationFromAnotherSystem
      v-if="showMigrationDialog"
      :show="showMigrationDialog"
      @close="showMigrationDialog = false"
      @confirm="handleMigrationConfirm"
    />
    <SetupCompleteDialog
      :show="$store.state.setupCompleteDialogVisible"
      @close="closeSetupCompleteDialog"
      @go="goToDashboardFromSetupComplete"
    />
    <CustomOnboarding ref="customOnboarding" />
  </v-app>
</template>

<script>
import MigrationFromAnotherSystem from '@/components/dialogs/MigrationFromAnotherSystem.vue';
import SetupCompleteDialog from '@/components/dialogs/SetupCompleteDialog.vue';
import WelcomeDialog from '@/components/dialogs/WelcomeDialog.vue';
import NavigationNavbar from '@/components/layout/navbar/index.vue';
import NavigationDrawer from '@/components/navigation/index.vue';
import CustomOnboarding from '@/components/onboarding/CustomOnboarding.vue';
import Api from '@/services/Api';
import { Environment } from '@/services/Environment';
import { resolveBlockedRedirect } from '@/router/academyStatusGuard';

export default {
  data() {
    return {
      inApp: this.$route.query.inApp,
      loadedVersion: null,
      permissionsLoaded: false,
      drawer: true,
      minimizedDrawer: true,
      paymentsReady: false,
      darkTheme: false,
      techSupportDialog: false,
      quickSearchItems: [],
      quickSearchQuery: null,
      feedbackContents: null,
      sendFeedbackDialog: false,
      crumbs: [],
      newAppAlertDialog: false,
      loadingQuickSearch: false,
      stripeAccountId: null,
      appModules: ['all'],
      appVersion: Environment.get('VUE_APP_VERSION'),
      showWelcomeDialog: false,
      showMigrationDialog: false,
      onboardingHomeValidationRunning: false,
    };
  },
  components: {
    NavigationDrawer,
    NavigationNavbar,
    WelcomeDialog,
    MigrationFromAnotherSystem,
    SetupCompleteDialog,
    CustomOnboarding,
  },
  mounted() {
    const oldUrl = 'https://bjjlink.com';
    //const oldUrl = 'https://localhost:8081';
    const currentUrl = window.location.origin;
    if (currentUrl == oldUrl) {
      this.newAppAlertDialog = true;
    }

    if (this.isSmallerDisplay && this.isSmallerDisplay()) {
      this.drawer = false;
      this.minimizedDrawer = true;
    }

    let scrollTimer = null;
    this.mainScrollHandler = () => {
      document.documentElement.classList.add('is-scrolling');
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        document.documentElement.classList.remove('is-scrolling');
      }, 800);
    };
    window.addEventListener('scroll', this.mainScrollHandler, {
      passive: true,
    });

    this.$watch(
      () => this.$vuetify.theme.dark,
      (dark) => {
        document.documentElement.classList.toggle('app-dark-theme', dark);
      },
      { immediate: true },
    );
  },
  beforeDestroy() {
    if (this.mainScrollHandler) {
      window.removeEventListener('scroll', this.mainScrollHandler);
    }
  },
  computed: {
    filteredQuickSearchItems() {
      // If there's no search query, return the original list
      if (!this.quickSearchQuery) {
        return this.quickSearchItems;
      }

      const searchLower = this.quickSearchQuery.toLowerCase();
      const filtered = [];
      let currentHeader = null;

      this.quickSearchItems.forEach((item) => {
        if (item.header) {
          // Store the current header
          currentHeader = item;
        } else if (item.text && item.text.toLowerCase().includes(searchLower)) {
          // If the item matches the search...
          // and its header hasn't been added yet, add it first.
          if (currentHeader) {
            filtered.push(currentHeader);
            currentHeader = null; // Set to null to avoid adding it again
          }
          // Then add the matching item.
          filtered.push(item);
        }
      });
      return filtered;
    },
  },
  // Onboarding enforcement moved to `src/router/index.js` (Home route guard).
  created() {
    const rawPath = window.location.pathname;
    const isLoggingOut = rawPath.indexOf('logout') >= 0;
    const isLoggingIn = rawPath.indexOf('login') >= 0; // Standard /login
    const isHandshake =
      rawPath.indexOf('applogin') >= 0 || rawPath.indexOf('triallogin') >= 0;

    if (isLoggingOut || isLoggingIn || isHandshake) {
      console.log(
        'App.vue: Logic Shielded for Auth Route. Stopping execution.',
      );

      // If we are on login, we still want the theme to look right, but nothing else.
      this.$vuetify.theme.dark = this.darkTheme;
      return;
    }

    this.getInitialBreadcrumb();

    // 3. MAIN LOGIC
    // Now this is safe because it only runs on "Internal" pages.
    if (this.$store.state.managedAcademy) {
      this.loadAcademyContext();
    } else {
      // We don't even need the "isLoggingIn" check here anymore
      // because the Shield above already caught it.
      this.$store.dispatch('setManagedAcademy', null);
      this.navigateTo({ name: 'noacademy' });
    }

    // 4. APP SESSION HANDLING (Kept)
    if (this.inApp && this.inApp == 'true') {
      this.$store.dispatch('setInApp', true);
    } else if (!this.loadedVersion) {
      this.$store.dispatch('setInApp', false);
    }

    // 5. RELEASE COUNT (Kept)
    if (this.$store.state.managedAcademy) {
      Api()
        .get('releases/project/bjjlink-admin/new')
        .then((response) => {
          this.$store.dispatch('setReleasesCount', response.data.count);
        });
    }
  },
  methods: {
    loadAcademyContext() {
      // 1. Academy Detail Logic
      Api()
        .get('admins/academy/' + this.$store.state.selectedAcademy)
        .then((response) => {
          if (response && response.data) {
            this.paymentsReady = response.data.paymentsReady;
            this.appModules = response.data.modules;
            this.stripeAccountId = response.data.stripeAccountId;
          }
        });

      this.loadQuickSearch();

      // 2. Main Admin Status Logic
      Api()
        .get(
          'admins/v3/managedacademy/' +
            this.$store.state.managedAcademy +
            '/get-academy-admin-status',
        )
        .then((response) => {
          const data = response.data;
          this.$store.dispatch('setAcademyAdminStatus', data ?? null);

          if (data) {
            if (data.language) {
              this.$store.dispatch('setLanguage', data.language);
              this.$i18n.locale = data.language;
            }

            if (data.userImage)
              this.$store.dispatch('setUserImage', data.userImage);

            if (data.email) this.$store.dispatch('setUserEmail', data.email);

            this.$store.dispatch(
              'setIsOnboardingUser',
              data.isOnboardingUser ?? false,
            );

            this.setColorTheme(data.darkMode);

            // Navigation Guards
            if (!data.isAdmin) this.navigateTo({ name: 'notadmin' });
            const blockedRedirect = resolveBlockedRedirect(this.$route.name, data);
            if (blockedRedirect) this.navigateTo(blockedRedirect);


            if (typeof data.settings?.adminPlusVersion === 'boolean') {
              this.$store.dispatch(
                'setAdminPlusVersion',
                data.settings.adminPlusVersion,
              );
            }
            this.$store.dispatch(
              'setMigrationDialogViewed',
              !!data.settings?.migrationDialogViewed,
            );

            if (data.settings?.isUserAdminPlus) {
              this.$store.dispatch('setIsUserAdminPlus', true);

              if (!this.$store.state.migrationDialogViewed) {
                this.$nextTick(() => {
                  this.showWelcomeDialog = true;
                });
              }
            }

            // 3. Permissions Logic (UNLOCKS THE UI)
            Api()
              .get(
                'admins/academy/' +
                  this.$store.state.managedAcademy +
                  '/admin/' +
                  this.$store.state.user._id +
                  '/permissions',
              )
              .then((response) => {
                this.permissionsLoaded = true;
                this.$store.dispatch(
                  'setPermissions',
                  response.data.permissions,
                );
              });
            // Check if this academy owns any groups — used to show the
            // Academy Groups sidebar entry for app admins regardless of permissions
            Api()
              .get(
                'groups/managedacademy/' +
                  this.$store.state.managedAcademy +
                  '/groups',
              )
              .then((groupsRes) => {
                this.$store.dispatch(
                  'setIsAcademyGroupOwner',
                  !!(groupsRes.data.groups && groupsRes.data.groups.length > 0),
                );
              })
              .catch(() => {
                this.$store.dispatch('setIsAcademyGroupOwner', false);
              });
          }
        })
        .catch((err) => {
          console.log('Error checking is_admin on App: ', err);
          this.$store.dispatch('setAcademyAdminStatus', null);
          this.$store.dispatch('setManagedAcademy', null);
          this.navigateTo({ name: 'noacademy' });
        });
    },
    openDrawerMobile() {
      if (this.isSmallerDisplay && this.isSmallerDisplay()) {
        this.drawer = true;
        this.minimizedDrawer = false;
      }
    },
    setShowWelcomeDialog() {
      this.showWelcomeDialog = true;
    },
    setShowMigrationDialog() {
      console.log('Setting showMigrationDialog to true');
      this.showMigrationDialog = true;
    },
    async startOnboarding() {
      console.log('Starting custom onboarding');
      if (!this.isSmallerDisplay()) {
        this.minimizedDrawer = false;
      }
      await this.$nextTick();
      const timeoutToWaitDrawerToBeOpen = 500;
      await new Promise((resolve) =>
        setTimeout(resolve, timeoutToWaitDrawerToBeOpen),
      );
      const customOnboarding = /** @type {any} */ (this.$refs.customOnboarding);
      if (customOnboarding && typeof customOnboarding.start === 'function') {
        customOnboarding.start();
      }
    },
    expandNavForTour() {
      if (!this.isSmallerDisplay()) {
        this.minimizedDrawer = false;
      }
    },
    async reloadAndRefresh(releases) {
      const currentVersion = this.appVersion;
      const response = await Api().get(
        'utils/version/bjjlink-admin/latest?patch=true',
      );
      const backendVersion = response.data;
      if (
        backendVersion &&
        backendVersion.version &&
        backendVersion.version != currentVersion
      ) {
        console.log(
          `New Version Detected: ${backendVersion.version}. Forcing Webview Refresh...`,
        );
        if (window.navigator && navigator.serviceWorker) {
          navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
              for (let registration of registrations) {
                registration.unregister();
              }
            });
        }
        window.sessionStorage.clear();
        const url = new URL(window.location.href);
        url.searchParams.set('v', backendVersion.version);
        url.searchParams.set('t', Date.now());
        window.location.replace(url.toString());
      } else if (releases) {
        this.goToReleases();
      }
    },
    downloadNewApp() {
      if (this.getOS() == 'Android')
        this.copyToClipboard(
          'https://play.google.com/store/apps/details?id=com.mixedmartialarts.bjjlinkconnect',
        );
      else if (this.getOS() == 'iOS')
        this.copyToClipboard(
          'https://apps.apple.com/us/app/bjjlink/id6743326231',
        );
    },
    reloadNewApp() {
      window.location.replace('https://admin.bjjlink.com');
    },
    dismissWelcome(option) {
      console.log('Dismiss welcome dialog with option: ', option);
      this.showWelcomeDialog = false;
      if (!option) {
        return;
      }
      if (option === 'migrateFromAnotherSystem') {
        this.setShowMigrationDialog();
        return;
      }
      if (this.$store.state.managedAcademy) {
        Api()
          .patch('users/settings', { migrationDialogViewed: true })
          .then(() => {
            this.$store.dispatch('setMigrationDialogViewed', true);
            this.navigateTo({
              name: 'academysetup',
            });
          });
      }
    },
    handleMigrationConfirm() {
      console.log('User confirmed migration from another system');
      this.showMigrationDialog = false;

      if (this.$store.state.managedAcademy) {
        Api()
          .patch('users/settings', { migrationDialogViewed: true })
          .then(() => {
            this.$store.dispatch('setMigrationDialogViewed', true);
          });
      }
    },
    async appLogin(jsonString) {
      if (!jsonString) {
        return;
      }
      try {
        this.$store.dispatch('setInApp', true);
        const data = JSON.parse(jsonString);
        const token = data ? data.token : null;
        if (token && (!localStorage.token || localStorage.token != token)) {
          this.$store.dispatch('setToken', data.token);
          this.$store.dispatch('setUser', data.user);
          this.$store.dispatch('setName', data.user.fName);
          this.$store.dispatch('setHidden', data.user.hidden);
          this.loadedVersion = 'v4';

          this.$store.dispatch(
            'setUserImage',
            data.user.userImageSmall
              ? data.user.userImageSmall
              : data.user.userImage,
          );
          this.$store.dispatch('setLanguage', data.user.lang);
          this.$i18n.locale = data.user.lang;
          localStorage.token = data.token;
          localStorage.hasAgreements = true;
          console.log('We are logged in! Going home with valid token.');
          if (this.$store.state.managedAcademy) {
            Api()
              .get(
                'admins/managedacademy/' +
                  this.$store.state.managedAcademy +
                  '/manager/me',
              )
              .then((response) => {
                const loggedUser = response.data.data;
                if (loggedUser) {
                  this.$store.dispatch('setLanguage', loggedUser.language);
                  this.$i18n.locale = loggedUser.language;
                  this.darkTheme = loggedUser.darkMode;
                  this.$vuetify.theme.dark = this.darkTheme;
                  let theme = 'light';
                  if (this.darkTheme) theme = 'dark';
                  this.$store.dispatch('setTheme', theme);
                  console.log(
                    'Admin window.replace to with managed academy: ',
                    process.env.VUE_APP_PUBLIC_PATH,
                  );
                  window.location = process.env.VUE_APP_PUBLIC_PATH;
                  window.location.replace(process.env.VUE_APP_PUBLIC_PATH);
                }
              });
          } else {
            console.log(
              'Admin window.replace to w/o managed academy: ',
              process.env.VUE_APP_PUBLIC_PATH,
            );
            window.location.replace(process.env.VUE_APP_PUBLIC_PATH);
          }
        } else {
          console.log('We did not try to log in.');
        }
      } catch (err) {
        console.log('Error applogin: " ', err);
      }
    },
    breadcrumbRoute(data) {
      this.navigateTo(data, true);
    },
    getInitialBreadcrumb() {
      this.crumbs.push(this.getBreadcrumbItem(this.$route));
      this.$router.crumbs = this.crumbs;
    },
    refreshQuickSearch() {
      this.loadQuickSearch();
    },
    closeNav() {
      if (this.isSmallerDisplay()) {
        this.drawer = false;
        this.minimizedDrawer = true;
      }
    },
    openTechSupport() {
      if (this.isSmallerDisplay()) {
        this.techSupportDialog = true;
      } else {
        window.open('https://bjjlink.tawk.help/');
      }
    },
    sendFeedback() {
      Api()
        .post(
          'admins/academy/' + this.$store.state.managedAcademy + '/feedback',
          {
            contents: this.feedbackContents,
          },
        )
        .then(() => {
          this.showAlert('success', this.$t('message.success'));
          this.sendFeedbackDialog = null;
          this.feedbackContents = null;
        });
    },
    loadQuickSearch() {
      this.loadingQuickSearch = true;
      Api()
        .post(
          'admins/academy/' +
            this.$store.state.managedAcademy +
            '/quicksearch/items',
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            //data.data
            let quickSearchItems = [{ header: this.$t('message.members') }];
            let members = [];
            let visitors = [];
            let tmp = data.data;
            if (tmp && tmp.length) {
              for (const i of tmp) {
                if (i.type == 'member') members.push(i);
                else visitors.push(i);
              }
            }

            //Members
            members.sort(function (a, b) {
              if (a.text > b.text) return 1;
              else if (a.text < b.text) return -1;
              return 0;
            });
            quickSearchItems = quickSearchItems.concat(members);

            //Visitors
            visitors.sort(function (a, b) {
              if (a.text > b.text) return 1;
              else if (a.text < b.text) return -1;
              return 0;
            });
            quickSearchItems.push({ header: this.$t('message.visitors') });
            quickSearchItems = quickSearchItems.concat(visitors);

            //Menus
            tmp = [];
            tmp.push({
              type: 'section',
              icon: 'creditcard',
              text: this.$t('message.transactions'),
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'creditcard',
              text: this.$t('message.memberships'),
              id: 1,
            });
            tmp.push({
              type: 'section',
              icon: 'creditcard',
              text: this.$t('message.purchases'),
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'creditcard',
              text: this.$t('message.refunds'),
              id: 3,
            });
            tmp.push({
              type: 'section',
              icon: 'creditcard',
              text: this.$t('message.expenses'),
              id: 4,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-cart-outline',
              text: this.$t('message.products'),
              action: 'productstabs',
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-sitemap-outline',
              text: this.$t('message.flowcharts'),
              action: 'flowcharts',
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-video-outline',
              text: this.$t('message.videos'),
              action: 'videos',
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-finance',
              text: this.$t('message.leads'),
              action: 'growthcentral',
              id: 1,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-finance',
              text: this.$t('message.visitors'),
              action: 'growthcentral',
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-finance',
              text: this.$t('message.inquiries'),
              action: 'growthcentral',
              id: 3,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-calendar',
              text: this.$t('message.calendar'),
              action: 'calendar',
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-calendar',
              text: this.$t('message.academy_events'),
              action: 'calendar',
              id: 1,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-calendar',
              text: this.$t('message.schedule'),
              action: 'calendar',
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-calendar',
              text: this.$t('message.add_class'),
              action: 'calendar',
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.members'),
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.pending'),
              id: 1,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.alumni'),
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.visitors'),
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.prospects'),
              id: 3,
            });
            tmp.push({
              type: 'section',
              icon: 'users',
              text: this.$t('message.rejected'),
              id: 4,
            });
            tmp.push({
              type: 'section',
              icon: 'documents',
              text: this.$t('message.completed_documents'),
              id: 0,
            });
            tmp.push({
              type: 'section',
              icon: 'documents',
              text: this.$t('message.documents'),
              id: 1,
            });
            tmp.push({
              type: 'section',
              icon: 'documents',
              text: this.$t('message.packages'),
              id: 2,
            });
            tmp.push({
              type: 'section',
              icon: 'documents',
              text: this.$t('message.visitor_waiver'),
              id: 3,
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-check-circle-outline',
              text: this.$t('message.attendance'),
              action: 'viewattendance',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-map-marker-outline',
              text: this.$t('message.checkins'),
              action: 'checkins',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: 'LMS',
              action: 'syllabusbuilder',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.syllabus'),
              action: 'syllabusbuilder',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.academy_info'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.tablet_credentials'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.admins'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.sent_emails'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.configuration'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.admins_activity'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.contact_form'),
              action: 'academy',
            });
            tmp.push({
              type: 'section',
              icon: 'mdi-tune-vertical',
              text: this.$t('message.academy_colors'),
              action: 'academy',
            });

            tmp.sort(function (a, b) {
              if (a.text > b.text) return 1;
              else if (a.text < b.text) return -1;
              return 0;
            });

            quickSearchItems.push({ header: 'Menu' });
            quickSearchItems = quickSearchItems.concat(tmp);

            this.quickSearchItems = quickSearchItems;
            this.loadingQuickSearch = false;
          }
        });
    },
    goToQuickSearchItem(item) {
      if (item.type == 'member') {
        this.goToMember(item.id, item.name);
      } else if (item.type == 'visitor' || item.type == 'prospect') {
        this.goToVisitor(item.id, item.text);
      } else if (item.type == 'section') {
        if (item.action) {
          this.navigateTo({
            name: item.action,
            params: { tabId: item.id },
          });
        } else if (item.icon == 'users') {
          this.navigateTo({
            name: 'memberstab',
            params: { tabId: item.id },
          });
        } else if (item.icon == 'creditcard') {
          this.navigateTo({
            name: 'transactionstab',
            params: { tabId: item.id },
          });
        } else if (item.icon == 'documents') {
          this.navigateTo({
            name: 'waiverstab',
            params: { tabId: item.id },
          });
        }
      }
    },
    toggleThemeColor() {
      this.darkTheme = !this.darkTheme;
      this.$vuetify.theme.dark = this.darkTheme;
      //push color update
      let theme = 'light';
      if (this.darkTheme) theme = 'dark';
      if (this.$store.state.managedAcademy) {
        Api()
          .patch('users/theme/toggle')
          .then(() => {
            this.$store.dispatch('setTheme', theme);
          });
      } else {
        this.navigateTo({ name: 'noacademy' });
      }
    },
    async toggleAdminPlusVersion() {
      const newValueAdminPlus = !this.$store.state.adminPlusVersion;

      if (this.isSmallerDisplay()) {
        this.drawer = false;
        this.minimizedDrawer = true;
      }

      if (this.$store.state.managedAcademy) {
        try {
          await Api().patch('users/settings', {
            adminPlusVersion: newValueAdminPlus,
          });
          this.$store.dispatch('setAdminPlusVersion', newValueAdminPlus);
        } catch (_e) {
          // keep existing value on error
        }
      } else {
        this.navigateTo({ name: 'noacademy' });
      }

      if (!newValueAdminPlus) {
        this.navigateTo({ name: 'home' });
        return;
      }

      const isOnboardingUser = this.$store.state.isOnboardingUser;
      this.navigateTo({ name: isOnboardingUser ? 'academysetup' : 'home' });
    },
    setColorTheme(darkMode) {
      this.darkTheme = darkMode;
      this.$vuetify.theme.dark = darkMode;
      //push color update
      let theme = 'light';
      if (this.darkTheme) theme = 'dark';
      if (this.$store.state.managedAcademy) {
        this.$store.dispatch('setTheme', theme);
      }
    },
    goToReleases() {
      this.navigateTo({ name: 'releases' });
      this.closeNav();
    },
    closeSetupCompleteDialog() {
      this.$store.dispatch('setSetupCompleteDialogVisible', false);
    },
    goToDashboardFromSetupComplete() {
      this.closeSetupCompleteDialog();
      this.navigateTo({ name: 'home' });
    },
    backToBjjlink() {
      window.location = '/';
    },
    toTablet() {
      window.open('/tablet/login');
    },
    isSpecialRoute() {
      if (
        this.$route.path == '/home' ||
        this.$route.path == '/logout' ||
        this.$route.path == '/secure/waiver' ||
        this.$route.path == '/notadmin' ||
        this.$route.name == 'qrcode' ||
        this.$route.path.indexOf('/qr') >= 0 ||
        this.$route.path.indexOf('/applogin/') >= 0 ||
        this.$route.path == '/secure/pickacademy' ||
        this.$route.path == '/secure/noacademy' ||
        this.$route.path == '/secure/checkin' ||
        this.$route.path.indexOf('print') >= 0 ||
        this.$route.path == '/secure/waver' ||
        this.$route.path == '/login' ||
        this.$route.path.indexOf('/triallogin/') >= 0
      ) {
        return true;
      } else {
        return false;
      }
    },
    logout() {
      this.$store.dispatch('setToken', null);
      this.$store.dispatch('setUser', null);
      this.$store.dispatch('setInApp', false);
      this.$store.dispatch('setSelectedAcademy', null);
      this.$store.dispatch('setManagedAcademy', null);
      this.$store.dispatch('setStripeAccountId', null);
      localStorage.removeItem('vuex');
      localStorage.removeItem('token');
      window.location.replace('/login?to=admin');
    },
  },
};
</script>

<style>
@font-face {
  font-family: 'Antartica';
  src: url('./assets/fonts/Antarctica-VF.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Roboto';
  src: url('./assets/fonts/Roboto-Regular.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
}

@font-face {
  font-family: 'Roboto';
  src: url('./assets/fonts/Roboto-Medium.woff2') format('woff2');
  font-weight: 500;
  font-style: normal;
}

@font-face {
  font-family: 'Roboto';
  src: url('./assets/fonts/Roboto-Bold.woff2') format('woff2');
  font-weight: 700;
  font-style: normal;
}

@font-face {
  font-family: 'Roboto';
  src: url('./assets/fonts/Roboto-Light.woff2') format('woff2');
  font-weight: 300;
  font-style: normal;
}

.font-roboto {
  font-family: 'Roboto', sans-serif !important;
  font-weight: 400;
}

.font-roboto-medium {
  font-family: 'Roboto', sans-serif !important;
  font-weight: 500;
}

.font-roboto-bold {
  font-family: 'Roboto', sans-serif !important;
  font-weight: 700;
}

.font-roboto-light {
  font-family: 'Roboto', sans-serif !important;
  font-weight: 300;
}

#app {
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.v-application .pa-4 {
  padding: 4px !important;
}

.app-footer {
  bottom: 0;
  left: 5px;
}

.clickable {
  cursor: pointer;
}

.hero-button {
  margin-top: -4px;
}

.error-message {
  color: white;
  font-weight: bold;
  background-color: #ff5252;
  border: 1px solid #ff5252;
  margin: 5px;
  border-radius: 5px;
  cursor: pointer;
}

.warn-message {
  color: white;
  font-weight: bold;
  background-color: #fb8c00 !important;
  border: 1px solid #fb8c00 !important;
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 5px;
  margin-bottom: 5px;
  border-radius: 5px;
  cursor: pointer;
}

.page-description {
  overflow-y: auto;
  max-height: 250px;
  overflow-x: auto;
  white-space: pre-wrap;
  white-space: -moz-pre-wrap;
  white-space: -pre-wrap;
  white-space: -o-pre-wrap;
  word-wrap: break-word;
}

.bjjlink_alert {
  font-family: 'Inter', sans-serif;
  font-size: medium;
  min-width: 400px;
}

.logo-cropped {
  width: 20px;
  /* width of container */
  height: 20px;
  /* height of container */
  overflow: hidden;
}

.logo-cropped img {
  margin: -12px 0px 0px -10px;
  width: 40px;
}

.bjjlink-card {
  margin-top: 10px;
}

.border-light {
  border: 1px solid #e0e0e0;
}

.border-dark {
  border: 1px solid #424242;
}

.supporting-text {
  color: gray;
  font-size: small;
}

.border-bottom {
  border-bottom: 2px solid #f5f5f5;
}

.hero-icon-button {
  margin-top: -8px;
}

.made-logo {
  margin-left: 1.4rem !important;
  width: 100%;
  text-align: center;
  margin: 0 auto;
}

.admin-logo {
  margin-left: 1.4rem !important;
  width: 100%;
  text-align: center;
  margin: 0 auto;
}

.support-iframe {
  display: block;
  /* iframes are inline by default */
  height: 100vh;
  /* Set height to 100% of the viewport height */
  width: 100vw;
  /* Set width to 100% of the viewport width */
  border: none;
  /* Remove default border */
  background: lightyellow;
  /* Just for styling */
}

.rotated {
  transform: rotate(90deg);
}

.app-floating-back-btn {
  position: absolute;
  left: 0px;
  top: 180px;
  transform: translate(-50%, -50%);
  z-index: 20;
  width: 38px !important;
  height: 38px !important;
  min-width: 38px !important;
  border-radius: 19px !important;
  background: #fafafa !important;
  border: 1px solid #eeeeee !important;
  box-shadow: none !important;
}

.theme--dark .app-floating-back-btn {
  background: #212121 !important;
  border: 1px solid #616161 !important;
}

/* Vuetify 2 renders v-checkbox / v-radio labels as .v-label with
   rgba(255,255,255,0.7) in dark mode, which fails WCAG AA contrast.
   Override to full theme text color across all checkboxes in the app. */
.theme--dark .v-input--selection-controls .v-label {
  color: var(--v-plusTextPrimary-base) !important;
}

.theme--dark .v-input--switch .v-input--switch__track {
  opacity: 1 !important;
  background-color: var(--v-plusGreyDarken3-base) !important;
}

.theme--dark .v-input--switch.v-input--is-dirty .v-input--switch__track {
  opacity: 1 !important;
  background-color: var(--v-plusIndigoLighten1-base) !important;
}

.theme--dark .v-input--switch .v-input--switch__thumb {
  background-color: #fafafa !important;
}

.theme--dark .v-input--switch .v-label {
  color: #fafafa !important;
}

.theme--dark .v-input--switch .v-messages {
  color: #bdbdbd !important;
}

.page-subtitle {
  font-family: 'Roboto', sans-serif;
  font-size: 16px !important;
  color: #757575;
}

.theme--dark .page-subtitle {
  color: #bdbdbd !important;
}

.v-overlay--active .v-overlay__scrim {
  opacity: 1 !important;
  background-color: rgba(0, 0, 0, 0.85) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
}

.fit-content {
  width: fit-content !important;
}
</style>
